import { useState, useEffect } from 'react';
import { 
    Card, 
    ListGroup, 
    Button, 
    Alert, 
    Spinner, 
    Badge, 
    Row, 
    Col,
    Modal,
} from 'react-bootstrap';
import { 
    FaFilePdf, 
    FaFileImage, 
    FaFileAlt, 
    FaDownload, 
    FaClock, 
    FaTimes,
    FaExternalLinkAlt
} from 'react-icons/fa';
import { getAccountDocuments, openDocument } from '../../../service/api/Documents/accountDocumentService';
import { toast } from 'sonner';

// Composant pour afficher un document individuel
const DocumentItem = ({ document, onPreview }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState('');

    // Icônes selon le type de document
    const getDocumentIcon = () => {
        const type = document.type?.toLowerCase();
        const fileName = document.fileName?.toLowerCase();
        
        if (type === 'pdf' || fileName?.includes('.pdf')) {
            return <FaFilePdf className="text-danger me-2" size={18} />;
        } else if (type === 'image' || /\.(jpg|jpeg|png|gif)$/i.test(fileName)) {
            return <FaFileImage className="text-success me-2" size={18} />;
        } else {
            return <FaFileAlt className="text-primary me-2" size={18} />;
        }
    };

    // Calcul du temps restant
    useEffect(() => {
        const updateTimeRemaining = () => {
            const now = new Date();
            const expiry = new Date(document.expiryTime);
            const diffMs = expiry - now;
            const diffMins = Math.floor(diffMs / 60000);
            
            if (diffMins <= 0) {
                setIsExpired(true);
                setTimeRemaining('Expiré');
                return;
            }
            
            setIsExpired(false);
            if (diffMins < 60) {
                setTimeRemaining(`${diffMins} min`);
            } else {
                const diffHours = Math.floor(diffMins / 60);
                setTimeRemaining(`${diffHours} h`);
            }
        };

        updateTimeRemaining();
        const interval = setInterval(updateTimeRemaining, 60000);

        return () => clearInterval(interval);
    }, [document.expiryTime]);

    const handleViewDocument = async () => {
        if (isExpired) {
            toast.error('Le lien a expiré. Veuillez rafraîchir la page.');
            return;
        }

        setIsLoading(true);
        try {
            //  PASSER accountId et document complet
            await openDocument(document.accountId, document);
            toast.success('Document ouvert dans un nouvel onglet');
        } catch (error) {
            toast.error('Erreur lors de l\'ouverture du document');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ListGroup.Item className="document-item">
            <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-start flex-grow-1 me-3">
                    {getDocumentIcon()}
                    <div className="flex-grow-1">
                        <div className="fw-semibold text-dark">{document.displayName}</div>
                        <small className="text-muted d-block">
                            {document.fileName}
                        </small>
                        <div className="mt-1">
                            <Badge 
                                bg={isExpired ? "danger" : "light"} 
                                text={isExpired ? "white" : "dark"}
                                className="me-2"
                            >
                                <FaClock className="me-1" size={12} />
                                {timeRemaining}
                            </Badge>
                            <Badge bg="outline-secondary" className="text-capitalize">
                                {document.type?.toLowerCase()}
                            </Badge>
                        </div>
                    </div>
                </div>
                
                <div className="d-flex flex-column gap-2">
                    <Button
                        variant={isExpired ? "outline-danger" : "outline-primary"}
                        size="sm"
                        onClick={handleViewDocument}
                        disabled={isLoading || isExpired}
                        className="d-flex align-items-center"
                    >
                        {isLoading ? (
                            <Spinner animation="border" size="sm" className="me-1" />
                        ) : (
                            <FaExternalLinkAlt className="me-1" />
                        )}
                        Ouvrir
                    </Button>
                </div>
            </div>
        </ListGroup.Item>
    );
};

// Modal de prévisualisation
const DocumentPreviewModal = ({ show, onHide, document }) => {
    const [loading, setLoading] = useState(true);

    if (!document) return null;

    const isPdf = document.type?.toLowerCase() === 'pdf' || 
                  document.fileName?.toLowerCase().includes('.pdf');
    const isImage = document.type?.toLowerCase() === 'image' || 
                    /\.(jpg|jpeg|png|gif)$/i.test(document.fileName);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="document-preview-modal">
            <Modal.Header closeButton className="bg-dark text-white">
                <Modal.Title>
                    {isPdf ? <FaFilePdf className="me-2 text-danger" /> : 
                     isImage ? <FaFileImage className="me-2 text-success" /> : 
                     <FaFileAlt className="me-2 text-primary" />}
                    {document.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                {loading && (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Chargement du document...</p>
                    </div>
                )}
                
                <div style={{ height: '70vh', overflow: 'auto' }}>
                    {isPdf ? (
                        <iframe
                            src={document.url}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            onLoad={() => setLoading(false)}
                            title={document.name}
                        />
                    ) : isImage ? (
                        <img
                            src={document.url}
                            alt={document.name}
                            style={{ 
                                width: '100%', 
                                height: 'auto',
                                display: loading ? 'none' : 'block'
                            }}
                            onLoad={() => setLoading(false)}
                        />
                    ) : (
                        <div className="text-center py-5">
                            <FaFileAlt size={48} className="text-muted mb-3" />
                            <p>Ce type de document ne peut pas être prévisualisé.</p>
                            <Button 
                                variant="primary" 
                                href={document.url} 
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaExternalLinkAlt className="me-2" />
                                Ouvrir dans un nouvel onglet
                            </Button>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onHide}>
                    <FaTimes className="me-1" />
                    Fermer
                </Button>
                <Button 
                    variant="primary"
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FaExternalLinkAlt className="me-1" />
                    Ouvrir dans un nouvel onglet
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Composant principal des documents
const AccountDocuments = ({ accountId }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            setError(null);
            const docs = await getAccountDocuments(accountId);
            setDocuments(docs);
        } catch (err) {
            setError(err.message || 'Desole,Erreur lors du chargement des documents');
            // console.error('Erreur:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accountId) {
            fetchDocuments();
        }
    }, [accountId]);

    const refreshDocuments = () => {
        fetchDocuments();
        toast.info('Rafraîchissement des liens documents');
    };

    const handlePreview = (document) => {
        setPreviewDocument(document);
        setShowPreview(true);
    };

    const handleClosePreview = () => {
        setShowPreview(false);
        setPreviewDocument(null);
    };

    if (loading) {
        return (
            <Card className="mt-4 border-0 shadow-sm">
                <Card.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 mb-0 text-muted">Chargement des documents...</p>
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="mt-4 border-0 shadow-sm">
                <Card.Header className="bg-white text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 d-flex align-items-center">
                        <FaFilePdf className="me-2" />
                        Documents du compte
                    </h5>
                    <div>
                        <Badge bg="light" text="dark" className="me-2">
                            {documents.length} document(s)
                        </Badge>
                        <Button variant="outline-light" size="sm" onClick={refreshDocuments}>
                            <FaDownload className="me-1" />
                            Rafraîchir
                        </Button>
                    </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                    {error ? (
                        <Alert variant="warning" className="m-3 mb-0">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>{error}</span>
                                <Button variant="outline-warning" size="sm" onClick={fetchDocuments}>
                                    Réessayer
                                </Button>
                            </div>
                        </Alert>
                    ) : documents.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <FaFilePdf size={48} className="mb-3 opacity-25" />
                            <p className="mb-0">Aucun document disponible pour ce compte</p>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {documents.map((doc, index) => (
                                <DocumentItem 
                                    key={index} 
                                    document={doc} 
                                    onPreview={handlePreview}
                                />
                            ))}
                        </ListGroup>
                    )}
                    
                    {documents.length > 0 && (
                        <div className="p-3 bg-light border-top">
                            <Row className="align-items-center">
                                <Col>
                                    <small className="text-muted">
                                        <FaClock className="me-1" />
                                        Les liens expirent automatiquement après 30 minutes pour des raisons de sécurité.
                                    </small>
                                </Col>
                                <Col xs="auto">
                                    <small className="text-muted">
                                        Dernière mise à jour : {new Date().toLocaleTimeString()}
                                    </small>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal de prévisualisation */}
            <DocumentPreviewModal
                show={showPreview}
                onHide={handleClosePreview}
                document={previewDocument}
            />
        </>
    );
};

export default AccountDocuments;